<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('payroll.edit_payslips_templates') }}
      </oxd-text>

      <oxd-divider />

      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-input-field
            v-model="payslipsTemplates.templateName"
            :label="$t('payroll.payslips_templates_name')"
            :rules="rules.templateName"
            required
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-input-field
            v-model="payslipsTemplates.version"
            :label="$t('general.version')"
            :rules="rules.version"
            required
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-input-field
            v-model="payslipsTemplates.description"
            type="textarea"
            :label="$t('general.description')"
            :rules="rules.description"
            required
          />
        </oxd-form-row>
        <oxd-form-row>
          <oxd-input-field
            v-model="payslipsTemplates.isActive"
            type="select"
            :options="statusOptions"
            :label="$t('general.status')"
          />
        </oxd-form-row>
        <oxd-form-row>
          <file-upload-input
            v-model:newFile="payslipsTemplates.templateAttachmentNew"
            v-model:method="payslipsTemplates.method"
            :label="$t('payroll.payslips_file')"
            :button-label="$t('general.browse')"
            :file="payslipsTemplates.templateAttachment"
            :rules="rules.templateAttachment"
            :url="payslipsTemplates.downloadPath"
            :hint="$t('general.accepts_up_to_1mb')"
            :deletable="false"
            :accept="acceptFile"
          />
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import {required, maxFileSize} from '@ohrm/core/util/validation/rules';
import FileUploadInput from '@/core/components/inputs/FileUploadInput.vue';
import {
  PAYSLIP_TEMPLATE_DOCX_ACCEPT,
  normalizePayslipTemplateDocxAttachment,
  validPayslipTemplateDocxFile,
} from '@/orangehrmPayrollPlugin/util/payslipTemplateDocxFile';

const initialData = {
  templateName: '',
  version: '',
  isActive: null,
  description: '',
  templateAttachment: {
    id: null,
    filename: '',
    filetype: '',
    size: '',
  },
  templateAttachmentNew: {
    name: '',
    type: '',
    size: '',
    base64: '',
  },
  downloadPath: '',
  method: 'keepCurrent',
};

export default {
  components: {
    'file-upload-input': FileUploadInput,
  },
  props: {
    allowedFileTypes: {
      type: Array,
      required: true,
    },
    maxFileSize: {
      type: Number,
      required: true,
    },
    payslipTemplatesId: {
      type: Number,
      required: true,
    },
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/payslip-templates',
    );
    return {
      http,
    };
  },

  data() {
    return {
      isLoading: false,
      payslipsTemplates: {...initialData},
      rules: {
        templateName: [required],
        version: [required],
        description: [required],
        templateAttachment: [
          required,
          validPayslipTemplateDocxFile(),
          maxFileSize(this.maxFileSize),
        ],
      },
      statusOptions: [
        {
          id: 1,
          param: 'active',
          label: this.$t('general.active'),
        },
        {
          id: 2,
          param: 'inactive',
          label: this.$t('general.inactive'),
        },
      ],
      acceptFile: PAYSLIP_TEMPLATE_DOCX_ACCEPT,
    };
  },

  created() {
    this.isLoading = true;
    this.http
      .get(this.payslipTemplatesId)
      .then((response) => {
        const {data} = response.data;
        const status = data.isActive ? 'active' : 'inactive';
        this.payslipsTemplates.templateName = data.templateName;
        this.payslipsTemplates.description = data.description;
        this.payslipsTemplates.version = data.version;
        this.payslipsTemplates.isActive =
          this.statusOptions.find((item) => item.param === status) || null;
        this.payslipsTemplates.templateAttachment.id = data.id;
        this.payslipsTemplates.templateAttachment.filename = data.filename;
        this.payslipsTemplates.templateAttachment.filetype = data.fileType;
        this.payslipsTemplates.templateAttachment.size = data.size;
        this.payslipsTemplates.downloadPath = data.downloadPath
          .split('/')
          .filter(Boolean)
          .slice(0, -1)
          .join('/');
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onCancel() {
      navigate('/payroll/viewPayslipsTemplates');
    },
    hasNewTemplateAttachment() {
      const {method, templateAttachmentNew: attachment} =
        this.payslipsTemplates;
      return (
        method === 'replaceCurrent' &&
        !!attachment?.base64 &&
        !!attachment?.name
      );
    },

    onSave() {
      this.isLoading = true;
      this.http
        .update(this.payslipTemplatesId, {
          templateName: this.payslipsTemplates.templateName,
          version: this.payslipsTemplates.version.toString(),
          isActive: this.payslipsTemplates.isActive.param === 'active',
          description: this.payslipsTemplates.description,
          ...(this.hasNewTemplateAttachment() && {
            templateAttachment: normalizePayslipTemplateDocxAttachment(
              this.payslipsTemplates.templateAttachmentNew,
            ),
          }),
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.onCancel();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
